'use client';
import { useState, useEffect, useRef, useCallback, type ReactNode, type MouseEvent as ReactMouseEvent, type SyntheticEvent } from 'react';
import {
  Plus, Search, Upload, X, GripVertical, Play, Zap, Loader2,
  Download, Timer, HardDrive, Trash2, ImageIcon, Check,
  RotateCcw, ChevronDown, ChevronLeft, ChevronRight, ExternalLink, Clock, Eye,
  Calendar, Share2, FileJson, Shield, Instagram, Facebook, AlertTriangle, ListChecks,
} from 'lucide-react';
import type {
  ImageVideo, LibraryImage, ImagnImage, TextOverlay, VideoSize,
  OverlayPosition, WordBreaking, FontSize, GameRecapRun,
  RecapPreviewMatch, RecapReactionMatch, GoalScorer, GoalScorers, MusicChoice, CfbGame, RenderProgress,
} from '@/lib/types';
import type { SportConfig } from '@/lib/sports';
import { logoUrlFor, cleanScore } from '@/lib/sports';
import { resolveAbbr, hasLogo } from '@/lib/team-abbrs';
import { readJson } from '@/lib/http';
import MusicPicker, { WORLDCUP_DEFAULT_TRACK, nameForTrackUrl } from '@/components/MusicPicker';

const EMPTY_SCORERS: GoalScorers = { home: [], away: [] };

// Recap videos default to the bundled cinematic bed, music on — same as World Cup.
const RECAP_DEFAULT_MUSIC: MusicChoice = { enabled: true, url: WORLDCUP_DEFAULT_TRACK.url, name: WORLDCUP_DEFAULT_TRACK.name };

const musicFromVideo = (v: ImageVideo): MusicChoice => ({
  enabled: !!v.music_enabled,
  url: v.music_url ?? null,
  name: nameForTrackUrl(v.music_url),
});

type ScoreboardEdit = {
  team1: string; team2: string; score: string;
  goalScorers: GoalScorers;
  showScore: boolean; showFlags: boolean; showGoalScorers: boolean;
};

type RowEdit = { script: string; videoSize: VideoSize; overlay: TextOverlay; music: MusicChoice; scoreboard: ScoreboardEdit };

const scoreboardFromVideo = (v: ImageVideo): ScoreboardEdit => ({
  team1: v.team1 ?? '',
  team2: v.team2 ?? '',
  score: v.score ?? '',
  goalScorers: normalizeScorers(v.goal_scorers),
  showScore: v.show_score ?? true,
  showFlags: v.show_flags ?? true,
  showGoalScorers: v.show_goal_scorers ?? true,
});

// Coerce whatever the reaction prompt returned for `goal_scorers` into the
// strict { home: GoalScorer[]; away: GoalScorer[] } shape.
const normalizeScorers = (raw: unknown): GoalScorers => {
  const side = (arr: unknown): GoalScorer[] =>
    Array.isArray(arr)
      ? arr.map((e): GoalScorer => {
          if (typeof e === 'string') return { player: e, minute: '' };
          const o = (e ?? {}) as Record<string, unknown>;
          return { player: String(o.player ?? ''), minute: String(o.minute ?? '') };
        }).filter((s) => s.player.trim())
      : [];
  const r = (raw ?? {}) as Record<string, unknown>;
  return { home: side(r.home), away: side(r.away) };
};

type GenStage = 'idle' | 'preparing' | 'audio' | 'rendering' | 'done' | 'error';
interface GenState {
  stage: GenStage;
  progress: number;
  videoUrl?: string;
  fileSizeBytes?: number;
  startedAt?: number;
  finishedAt?: number;
  error?: string;
}

type PickerSource = 'imagn' | 'upload' | 'library';

interface ChosenImage {
  key: string;
  previewUrl: string;
  s3_url?: string;
  imagnId?: string;
  file?: File;
  caption?: string;
}

const VIDEO_SIZES: VideoSize[] = ['9:16', '16:9', '1:1', '4:5'];
const POSITIONS: OverlayPosition[] = ['top', 'center', 'bottom'];
const WORD_BREAKING: WordBreaking[] = ['one', 'few', 'several'];
const FONT_SIZES: FontSize[] = ['small', 'medium', 'big'];

const IMAGN_FETCH_LIMIT = 50;
const IMAGN_PER_PAGE = 24;
const IMAGN_MAX_RESULTS = 300;

const DEFAULT_OVERLAY: TextOverlay = {
  position: 'center',
  wordBreaking: 'one',
  size: 'big',
  color: '#FFFFFF',
};

const etToday = (): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());

const etHour = (): number =>
  Number(new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', hour: '2-digit', hourCycle: 'h23',
  }).format(new Date()));

// No league on this tab kicks off before lunchtime ET, so until then the day
// has nothing on it to recap yet.
const FIRST_KICKOFF_ET_HOUR = 12;

// How the tab waits on a run. A whole NFL Sunday — a grounded preview call, a
// reaction call per game four at a time, then a throttled Sportradar box score
// per game — measured at about three minutes for fourteen games; the timeout is
// well past that so a slow run is never mistaken for a dead one.
const RUN_POLL_INTERVAL_MS = 3000;
const RUN_POLL_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * The date the tab opens on: the last one that plausibly has finished games.
 *
 * Opening on the ET calendar date is wrong for exactly the hours these recaps
 * get cut. An operator working the Sunday slate at 00:16 ET on Monday was
 * handed Monday — a day whose games had not kicked off — and the reaction
 * prompt correctly returned nothing, which reads as "the tool is broken".
 * Before the first kickoff of a day, yesterday is the answer. The date picker
 * is right there either way.
 */
const defaultRecapDate = (): string => {
  const today = etToday();
  return etHour() < FIRST_KICKOFF_ET_HOUR ? shiftDate(today, -1) : today;
};

const shiftDate = (iso: string, days: number): string => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
};

const prettyDate = (iso: string): string => {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
};

// Merge a run's preview + reaction matches into one row per matchup.
interface Matchup {
  teams: string;
  preview?: RecapPreviewMatch;
  reaction?: RecapReactionMatch;
}
const mergeMatchups = (run: GameRecapRun): Matchup[] => {
  const norm = (s: string) => (s || '').toLowerCase().replace(/\s+/g, ' ').trim();
  const map = new Map<string, Matchup>();
  (run.preview_json ?? []).forEach((m) => {
    const k = norm(m.teams);
    const e = map.get(k) ?? { teams: m.teams };
    e.preview = m;
    map.set(k, e);
  });
  (run.reaction_json ?? []).forEach((m) => {
    const k = norm(m.teams);
    const e = map.get(k) ?? { teams: m.teams };
    e.reaction = m;
    if ((m.teams?.length ?? 0) > (e.teams?.length ?? 0)) e.teams = m.teams;
    map.set(k, e);
  });
  return [...map.values()];
};

// Split a "Home vs Away" label — the fallback when the model omitted the
// explicit abbreviation fields.
const parseTeams = (teams: string): [string, string] => {
  const parts = (teams || '').split(/\s+(?:vs?\.?|@|at)\s+/i);
  return [(parts[0] ?? '').trim(), (parts[1] ?? '').trim()];
};

interface ScriptMeta { team1: string; team2: string; score: string; goalScorers: GoalScorers }

const PREGAME_SCRIPT_TYPES = new Set(['preview', 'betting']);
const scriptHasScoreboard = (key: string | null): boolean =>
  !key || !PREGAME_SCRIPT_TYPES.has(key.split(':').pop() ?? '');

const videoHasScoreboard = (v: ImageVideo): boolean =>
  !!(v.team1?.trim() || v.team2?.trim() || v.score?.trim() ||
     (v.goal_scorers?.home?.length ?? 0) > 0 || (v.goal_scorers?.away?.length ?? 0) > 0);

const formatElapsed = (secs: number) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s.toString().padStart(2, '0')}s` : `${s}s`;
};
const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
const formatDuration = (secs: number) => {
  if (!isFinite(secs)) return '—';
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

function RatioBox({ size }: { size: VideoSize }) {
  const dims: Record<VideoSize, [number, number]> = {
    '9:16': [9, 16], '16:9': [16, 9], '1:1': [1, 1], '4:5': [4, 5],
  };
  const [w, h] = dims[size] ?? [9, 16];
  const scale = 16 / Math.max(w, h);
  return (
    <span className="block border-[1.5px] border-current rounded-[2px]" style={{ width: `${w * scale}px`, height: `${h * scale}px` }} />
  );
}

function PositionGlyph({ position }: { position: OverlayPosition }) {
  const bar = position === 'top' ? 'top-[2px]' : position === 'center' ? 'top-1/2 -translate-y-1/2' : 'bottom-[2px]';
  return (
    <span className="relative block w-3.5 h-4 border-[1.5px] border-current rounded-[2px]">
      <span className={`absolute left-[2px] right-[2px] h-[2.5px] bg-current rounded-full ${bar}`} />
    </span>
  );
}

function FontGlyph({ size }: { size: FontSize }) {
  const px = size === 'small' ? 10 : size === 'medium' ? 14 : 18;
  return <span className="font-black leading-none" style={{ fontSize: `${px}px` }}>A</span>;
}

// The team's logo as the Remotion composition will resolve it — same league
// folder, same abbreviation. Renders a warning stub when no file exists, which
// is the operator's cue to correct the abbreviation before generating.
function LogoThumb({ league, abbr }: { league: string; abbr: string }) {
  const src = abbr ? logoUrlFor(league, abbr) : null;
  // Track WHICH url failed rather than a bare boolean, so correcting the
  // abbreviation re-tries the new url without needing an effect to reset.
  const [brokenSrc, setBrokenSrc] = useState<string | null>(null);
  if (!src || brokenSrc === src) {
    return (
      <span
        className="w-6 h-6 shrink-0 rounded-[3px] bg-white/5 border border-white/10 flex items-center justify-center"
        title={abbr ? `No logo found at ${src}` : 'No team set'}
      >
        {abbr ? <AlertTriangle size={11} className="text-amber-500" /> : null}
      </span>
    );
  }
  // eslint-disable-next-line @next/next/no-img-element
  return <img src={src} alt="" title={src} onError={() => setBrokenSrc(src)} className="w-6 h-6 shrink-0 object-contain" />;
}

function ToggleChip({ label, active, onChange }: { label: string; active: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!active)}
      role="switch"
      aria-checked={active}
      className={`inline-flex items-center gap-1.5 rounded-full pl-1 pr-3 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors border ${
        active ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' : 'bg-white/[0.03] text-slate-500 border-white/10 hover:text-slate-300'
      }`}
    >
      <span className={`w-4 h-4 rounded-full flex items-center justify-center ${active ? 'bg-emerald-500 text-black' : 'bg-white/10'}`}>
        {active && <Check size={10} strokeWidth={3} />}
      </span>
      {label}
    </button>
  );
}

// Editable scorer list for one side. The `minute` column holds whatever the
// sport measures a scoring contribution by — a stat line for football, a game
// clock for hockey, a point total for basketball.
function ScorerList({
  side, teamName, league, scorers, onChange, clockPlaceholder, noun,
}: {
  side: 'home' | 'away';
  teamName: string;
  league: string;
  scorers: GoalScorer[];
  onChange: (next: GoalScorer[]) => void;
  clockPlaceholder: string;
  noun: string;
}) {
  const update = (i: number, patch: Partial<GoalScorer>) =>
    onChange(scorers.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const remove = (i: number) => onChange(scorers.filter((_, idx) => idx !== i));
  const add = () => onChange([...scorers, { player: '', minute: '' }]);
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-2">
      <div className="flex items-center gap-2">
        <LogoThumb league={league} abbr={teamName} />
        <span className="text-xs font-bold text-white truncate">{teamName || (side === 'home' ? 'Home' : 'Away')}</span>
      </div>
      {scorers.map((s, i) => (
        <div key={i} className="flex items-center gap-2">
          <input value={s.player} onChange={(e) => update(i, { player: e.target.value })} placeholder={noun.charAt(0).toUpperCase() + noun.slice(1)}
            className="flex-1 min-w-0 bg-slate-900/50 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white placeholder:text-slate-600 focus:border-white/30 outline-none" />
          <input value={s.minute} onChange={(e) => update(i, { minute: e.target.value })} placeholder={clockPlaceholder}
            className="w-20 shrink-0 text-center bg-slate-900/50 border border-white/10 rounded-md px-1 py-1.5 text-xs font-mono text-white placeholder:text-slate-600 focus:border-white/30 outline-none" />
          <button onClick={() => remove(i)} title={`Remove ${noun}`}
            className="shrink-0 p-1.5 rounded text-slate-600 hover:text-red-500 hover:bg-red-500/10 transition-colors"><X size={13} /></button>
        </div>
      ))}
      <button onClick={add}
        className="w-full flex items-center justify-center gap-1.5 rounded-md border border-dashed border-white/15 hover:border-white/35 hover:bg-white/[0.03] text-slate-400 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors">
        <Plus size={12} /> Add {noun}
      </button>
    </div>
  );
}

function StepHeader({ label, right }: { label: string; right?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap mb-3 min-h-[28px]">
      <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-300">{label}</h4>
      {right}
    </div>
  );
}

/** An AP Top 25 place, written the way a broadcast graphic writes it: "#7". */
function Rank({ n }: { n: number | null }) {
  if (!n) return null;
  return <span className="font-mono text-[10px] font-bold text-amber-500/90 mr-0.5">#{n}</span>;
}

function Segmented<T extends string>({
  options, value, onChange, labels, icon, compact,
}: {
  options: T[];
  value: T;
  onChange: (v: T) => void;
  labels?: Record<string, string>;
  icon?: (opt: T) => ReactNode;
  compact?: boolean;
}) {
  const layout = compact
    ? 'flex-row items-center gap-1.5 px-2.5 py-1'
    : icon
      ? 'flex-col items-center justify-center gap-1.5 w-[3.4rem] py-2'
      : 'flex-row items-center justify-center gap-1.5 px-3 py-1.5';
  return (
    <div className={`flex flex-wrap ${compact ? 'gap-1' : 'gap-1.5'}`}>
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            onClick={() => onChange(opt)}
            className={`flex rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all border ${layout} ${
              active ? 'bg-white/[0.12] text-white border-white/20' : 'text-slate-500 border-transparent hover:text-slate-300 hover:bg-white/[0.04]'
            }`}
          >
            {icon && (
              <span className={`flex items-center justify-center ${compact ? '' : 'h-[18px]'} ${active ? 'text-white' : 'text-slate-500'}`}>
                {icon(opt)}
              </span>
            )}
            <span>{labels?.[opt] ?? opt}</span>
          </button>
        );
      })}
    </div>
  );
}

function QueryChip({ q, onClick }: { q: string; onClick: (q: string) => void }) {
  return (
    <button
      onClick={() => onClick(q)}
      title="Search Imagn for this"
      className="group inline-flex items-center gap-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 rounded-full px-3 py-1 text-[10px] font-medium transition-colors max-w-full"
    >
      <Search size={10} className="shrink-0 opacity-70" />
      <span className="truncate">{q}</span>
    </button>
  );
}

function ScriptBlock({ label, text, onUse }: { label: string; text?: string; onUse?: (text: string) => void }) {
  if (!text) return null;
  return (
    <div className="bg-black/30 rounded-lg border border-white/10 p-3">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">{label}</span>
        {onUse && (
          <button
            onClick={() => onUse(text)}
            className="flex items-center gap-1 bg-amber-500/15 hover:bg-amber-500 hover:text-black border border-amber-500/40 text-amber-300 rounded-md px-2 py-1 text-[9px] font-black uppercase tracking-wider transition-colors"
            title="Use this as the Audio Script"
          >
            <Zap size={10} fill="currentColor" /> Use as script
          </button>
        )}
      </div>
      <p className="text-[11px] leading-relaxed text-slate-300">{text}</p>
    </div>
  );
}

function PublishBadge({ published, v }: { published: boolean; v: ImageVideo }) {
  if (published) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-600/20 text-emerald-400 text-[9px] font-black uppercase tracking-widest" title={v.published_at ? `Published ${new Date(v.published_at).toLocaleString()}` : 'Published'}>
        <Check size={9} /> Published
        {v.ig_media_id && <Instagram size={10} className="text-pink-400" />}
        {v.fb_post_id && <Facebook size={10} className="text-sky-400" />}
      </span>
    );
  }
  if (v.publish_error && (v.publish_attempts ?? 0) > 0) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-600/20 text-red-400 text-[9px] font-black uppercase tracking-widest" title={v.publish_error}>
        <X size={9} /> Publish failed
      </span>
    );
  }
  return null;
}

function ScriptChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      title="Use this script + suggest its image searches"
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors border ${
        active
          ? 'bg-amber-500 text-black border-amber-400'
          : 'bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border-amber-500/30'
      }`}
    >
      <Zap size={10} fill="currentColor" /> {label}
    </button>
  );
}

// Abbreviations for a matchup, preferring the explicit fields the prompt emits
// and falling back to parsing the "Home vs Away" label.
const abbrsFor = (league: string, mu: Matchup): [string, string] => {
  const src = mu.reaction ?? mu.preview;
  const [pHome, pAway] = parseTeams(mu.teams);
  const raw1 = String(src?.team1_abbr ?? pHome ?? '');
  const raw2 = String(src?.team2_abbr ?? pAway ?? '');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return [resolveAbbr(league as any, raw1), resolveAbbr(league as any, raw2)];
};

function QuickScriptSelect({
  matchups, keyPrefix, activeKey, onPick, league,
}: {
  matchups: Matchup[];
  keyPrefix: string;
  activeKey: string | null;
  onPick: (text: string, queries: string[] | undefined, key: string, meta: ScriptMeta) => void;
  league: string;
}) {
  if (matchups.length === 0) return null;
  return (
    <div className="space-y-2">
      {matchups.map((mu, mi) => {
        const [team1, team2] = abbrsFor(league, mu);
        const meta: ScriptMeta = {
          team1, team2,
          score: cleanScore(mu.reaction?.verified_score),
          goalScorers: normalizeScorers(mu.reaction?.goal_scorers),
        };
        return (
        <div key={mi} className="flex flex-wrap items-center gap-2 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
          <span className="flex items-center gap-1.5 mr-1">
            <LogoThumb league={league} abbr={team1} />
            <LogoThumb league={league} abbr={team2} />
          </span>
          <span className="text-xs font-bold text-white mr-1">{mu.teams}</span>
          {mu.preview?.preview_narrative_script && (
            <ScriptChip label="Preview" active={activeKey === `${keyPrefix}:${mi}:preview`}
              onClick={() => onPick(mu.preview!.preview_narrative_script!, mu.preview!.image_search_queries, `${keyPrefix}:${mi}:preview`, meta)} />
          )}
          {mu.reaction?.hot_take_high_temp && (
            <ScriptChip label="Hot take" active={activeKey === `${keyPrefix}:${mi}:hot`}
              onClick={() => onPick(mu.reaction!.hot_take_high_temp!, mu.reaction!.image_search_queries, `${keyPrefix}:${mi}:hot`, meta)} />
          )}
          {mu.preview?.betting_line_movement && (
            <ScriptChip label="Betting line" active={activeKey === `${keyPrefix}:${mi}:betting`}
              onClick={() => onPick(mu.preview!.betting_line_movement!, mu.preview!.image_search_queries, `${keyPrefix}:${mi}:betting`, meta)} />
          )}
          {mu.reaction?.recap_script_low_temp && (
            <ScriptChip label="Recap (Factual)" active={activeKey === `${keyPrefix}:${mi}:recap`}
              onClick={() => onPick(mu.reaction!.recap_script_low_temp!, mu.reaction!.image_search_queries, `${keyPrefix}:${mi}:recap`, meta)} />
          )}
        </div>
        );
      })}
    </div>
  );
}

export default function GameRecapTab({
  sport, videos, onRefresh, runs, onRefreshRuns,
}: {
  sport: SportConfig;
  videos: ImageVideo[];
  onRefresh: () => void;
  runs: GameRecapRun[];
  onRefreshRuns: () => void;
}) {
  const league = sport.league;
  const [showCreate, setShowCreate] = useState(false);

  const [chosen, setChosen] = useState<ChosenImage[]>([]);
  const [script, setScript] = useState('');
  const [videoSize, setVideoSize] = useState<VideoSize>('9:16');
  const [overlay, setOverlay] = useState<TextOverlay>(DEFAULT_OVERLAY);
  const [music, setMusic] = useState<MusicChoice>(RECAP_DEFAULT_MUSIC);

  // Scoreboard props. team1/team2 are LOGO ABBREVIATIONS — the composition
  // builds each emblem URL from the league plus the abbreviation.
  const [team1, setTeam1] = useState('');
  const [team2, setTeam2] = useState('');
  const [score, setScore] = useState('');
  const [goalScorers, setGoalScorers] = useState<GoalScorers>(EMPTY_SCORERS);

  const [showScore, setShowScore] = useState(true);
  const [showFlags, setShowFlags] = useState(true);
  const [showGoalScorers, setShowGoalScorers] = useState(true);

  const [selectedQueries, setSelectedQueries] = useState<string[]>([]);
  const [selectedScriptKey, setSelectedScriptKey] = useState<string | null>(null);

  const [date, setDate] = useState<string>(defaultRecapDate());
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);

  // CFB only: the day's schedule, and which game the operator picked.
  const [schedule, setSchedule] = useState<CfbGame[]>([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  // A Saturday runs to a hundred-plus games, nearly all of them between schools
  // nobody cuts a recap for, so the list opens filtered to the AP Top 25.
  const [top25Only, setTop25Only] = useState(true);
  const [rankingsAvailable, setRankingsAvailable] = useState(true);
  const [runningGameId, setRunningGameId] = useState<string | null>(null);

  const [source, setSource] = useState<PickerSource>('imagn');
  const [imagnQuery, setImagnQuery] = useState('');
  const [imagnResults, setImagnResults] = useState<ImagnImage[]>([]);
  const [imagnSearching, setImagnSearching] = useState(false);
  const [imagnLoadingMore, setImagnLoadingMore] = useState(false);
  const [imagnPage, setImagnPage] = useState(0);
  const searchTokenRef = useRef(0);
  const [lastSearched, setLastSearched] = useState<string | null>(null);
  const [library, setLibrary] = useState<LibraryImage[]>([]);
  const [libraryLoaded, setLibraryLoaded] = useState(false);

  const [viewing, setViewing] = useState<ImageVideo | null>(null);

  const [previewing, setPreviewing] = useState(false);
  const [gen, setGen] = useState<GenState>({ stage: 'idle', progress: 0 });
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const dragIndex = useRef<number | null>(null);
  const createRef = useRef<HTMLDivElement | null>(null);
  // Clicking a suggested-search chip should land on the image results, not the
  // top of the create panel — otherwise every chip click throws the operator
  // back up to "Pick a Script" and they have to scroll down again.
  const pickerRef = useRef<HTMLDivElement | null>(null);

  const [expandedRun, setExpandedRun] = useState<string | null>(null);
  const [runView, setRunView] = useState<Record<string, 'both' | 'preview' | 'reaction'>>({});

  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [rowGen, setRowGen] = useState<Record<string, { stage: 'rendering' | 'error'; progress: number }>>({});
  const [downloading, setDownloading] = useState<Record<string, boolean>>({});
  const [durations, setDurations] = useState<Record<string, number>>({});
  const [sizes, setSizes] = useState<Record<string, number>>({});
  const [editState, setEditState] = useState<Record<string, RowEdit>>({});
  const [publishedById, setPublishedById] = useState<Record<string, boolean>>({});
  const [publishedBusy, setPublishedBusy] = useState<Record<string, boolean>>({});
  const [pushing, setPushing] = useState<Record<string, boolean>>({});
  const [pushError, setPushError] = useState<Record<string, string>>({});

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  useEffect(() => {
    const seed: Record<string, boolean> = {};
    for (const v of videos) seed[v.id] = !!v.published_at;
    setPublishedById(seed);
  }, [videos]);

  useEffect(() => {
    if (!viewing) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setViewing(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewing]);

  // --- CFB schedule: load whenever the tab or the date changes ---
  const loadSchedule = useCallback(async (forDate: string) => {
    setScheduleLoading(true);
    setScheduleError(null);
    setSchedule([]);
    try {
      const res = await fetch(`/api/gamerecap/cfb-schedule?date=${forDate}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load the schedule');
      setSchedule(data.games ?? []);
      // Sportradar was unreachable for the poll, or that week's poll is not out
      // yet — every game then reads as unranked, so the filter has to fall open.
      setRankingsAvailable(data.rankingsAvailable !== false);
    } catch (e) {
      setScheduleError(e instanceof Error ? e.message : 'Failed to load the schedule');
    } finally {
      setScheduleLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!sport.onDemand) return;
    loadSchedule(date);
  }, [sport.onDemand, date, loadSchedule]);

  // A game counts as Top 25 if EITHER school is ranked — an unranked team
  // beating a ranked one is exactly the game worth a recap. The filter falls
  // open when no poll could be read, so a poll outage never leaves the operator
  // staring at an empty slate.
  const rankedSchedule = schedule.filter((g) => g.homeRank !== null || g.awayRank !== null);
  const top25Active = top25Only && rankingsAvailable;
  const visibleSchedule = top25Active ? rankedSchedule : schedule;

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    const start = Date.now();
    setElapsed(0);
    timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
  };
  const stopTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };

  const loadLibrary = async () => {
    try {
      const res = await fetch('/api/image-library');
      const data = await res.json();
      setLibrary(data.images ?? []);
    } catch (e) { console.error(e); }
    finally { setLibraryLoaded(true); }
  };

  const addChosen = (item: ChosenImage) =>
    setChosen((prev) => (prev.some((c) => c.key === item.key) ? prev : [...prev, item]));
  const removeChosen = (key: string) =>
    setChosen((prev) => {
      const item = prev.find((c) => c.key === key);
      if (item?.file) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((c) => c.key !== key);
    });
  const isChosen = (key: string) => chosen.some((c) => c.key === key);

  const fetchImagnPage = async (terms: string, offset: number): Promise<{ images: ImagnImage[]; total: number }> => {
    const res = await fetch('/api/imagn/search', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ terms, limit: IMAGN_FETCH_LIMIT, offset }),
    });
    const data = await res.json();
    return { images: data.images ?? [], total: data.total ?? 0 };
  };

  const searchImagn = async (queryOverride?: string) => {
    const q = (queryOverride ?? imagnQuery).trim();
    if (q.length < 2) return;
    const token = ++searchTokenRef.current;
    setImagnSearching(true);
    setImagnLoadingMore(false);
    setImagnResults([]);
    setImagnPage(0);
    try {
      const first = await fetchImagnPage(q, 0);
      if (searchTokenRef.current !== token) return;
      setLastSearched(q);
      const total = Math.min(first.total || first.images.length, IMAGN_MAX_RESULTS);
      const seen = new Set(first.images.map((i) => i.imageID));
      let all = first.images;
      setImagnResults(all);
      setImagnSearching(false);
      if (all.length < total) {
        setImagnLoadingMore(true);
        for (let offset = IMAGN_FETCH_LIMIT; offset < total && all.length < IMAGN_MAX_RESULTS; offset += IMAGN_FETCH_LIMIT) {
          const next = await fetchImagnPage(q, offset);
          if (searchTokenRef.current !== token) return;
          if (!next.images.length) break;
          const fresh = next.images.filter((i) => !seen.has(i.imageID));
          fresh.forEach((i) => seen.add(i.imageID));
          if (fresh.length === 0) continue;
          all = all.concat(fresh);
          setImagnResults([...all]);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (searchTokenRef.current === token) {
        setImagnSearching(false);
        setImagnLoadingMore(false);
      }
    }
  };

  const applyImagnQuery = (q: string) => {
    const wasOpen = showCreate;
    setShowCreate(true);
    if (!libraryLoaded) loadLibrary();
    setSource('imagn');
    setImagnQuery(q);
    searchImagn(q);
    // Scroll only when the picker is not already on screen: clicking a chip
    // that sits right above the results should not move the page at all.
    requestAnimationFrame(() => {
      const el = pickerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const visible = r.top >= 0 && r.top < window.innerHeight * 0.6;
      if (!wasOpen || !visible) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const applyScript = (text: string, queries?: string[], key?: string, meta?: ScriptMeta) => {
    setShowCreate(true);
    setScript(text);
    setSelectedQueries((queries ?? []).filter(Boolean));
    setSelectedScriptKey(key ?? null);
    if (meta) { setTeam1(meta.team1); setTeam2(meta.team2); setScore(meta.score); setGoalScorers(meta.goalScorers); }
    createRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // --- Run the two prompts. Whole-slate for the pro leagues; one game at a
  // time for CFB, where a day can carry a hundred-plus fixtures. ---
  const runPrompts = async (game?: CfbGame) => {
    if (running) return;
    setRunError(null);
    setRunning(true);
    setRunningGameId(game?.id ?? null);
    try {
      const payload: Record<string, unknown> = { sport: sport.key, date };
      if (game) {
        payload.game = {
          id: game.id,
          label: `${game.awayName} at ${game.homeName}`,
          // Both the aliases (already resolved to logo files by the schedule
          // route) and the school names — the run route matches the model's
          // team strings against the names to pick the right alias per side.
          homeAlias: game.homeAlias,
          awayAlias: game.awayAlias,
          homeName: game.homeName,
          awayName: game.awayName,
        };
      }
      const res = await fetch('/api/gamerecap/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      // The route answers as soon as the row exists; the prompts run on after
      // it. Everything below waits on the row, not on the request — which is
      // what keeps a two-minute NFL slate off nginx's proxy timeout.
      const { run } = await readJson<{ run: { id: string } | null }>(res, 'Running the prompts');
      onRefreshRuns();
      if (run?.id) {
        setExpandedRun(run.id);
        setShowCreate(true);
        if (!libraryLoaded) loadLibrary();
        const finished = await waitForRun(run.id);
        if (finished?.status === 'error') {
          setRunError(finished.preview_error && finished.reaction_error
            ? `Both prompts failed — ${finished.preview_error} | ${finished.reaction_error}`
            : finished.preview_error || finished.reaction_error || 'The run failed');
        }
      }
    } catch (e) {
      setRunError(e instanceof Error ? e.message : 'Failed to run prompts');
    } finally {
      setRunning(false);
      setRunningGameId(null);
    }
  };

  /**
   * Poll one run until it stops being `running`, refreshing the list as it
   * goes so the slate fills in while the operator watches.
   *
   * The timeout is a backstop for the app server being restarted mid-run,
   * which would otherwise leave the row `running` and this loop going forever.
   */
  const waitForRun = async (id: string): Promise<GameRecapRun | null> => {
    const deadline = Date.now() + RUN_POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, RUN_POLL_INTERVAL_MS));
      let run: GameRecapRun | null = null;
      try {
        const res = await fetch(`/api/gamerecap/run?id=${encodeURIComponent(id)}`);
        ({ run } = await readJson<{ run: GameRecapRun | null }>(res, 'Checking the run'));
      } catch {
        continue; // a blip in the polling is not the run failing — keep waiting
      }
      onRefreshRuns();
      if (run && run.status !== 'running') return run;
    }
    setRunError('The run is taking longer than expected — it may still finish. Check “Generated Outputs” in a minute.');
    return null;
  };

  const deleteRun = async (id: string) => {
    try {
      await fetch(`/api/gamerecap/run?id=${id}`, { method: 'DELETE' });
      onRefreshRuns();
    } catch (e) { console.error(e); }
  };

  const addImagn = (img: ImagnImage) => {
    const key = `imagn:${img.imageID}`;
    if (isChosen(key)) { removeChosen(key); return; }
    addChosen({ key, previewUrl: img.previewUrl || img.thumbUrl, imagnId: img.imageID, caption: img.caption });
  };

  const handleUpload = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    Array.from(files).forEach((f) =>
      addChosen({ key: `upload:${f.name}:${f.size}:${f.lastModified}`, previewUrl: URL.createObjectURL(f), file: f, caption: f.name }),
    );
  };

  const onDragStart = (i: number) => { dragIndex.current = i; };
  const onDrop = (i: number) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === i) return;
    setChosen((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(i, 0, moved);
      return next;
    });
  };

  const previewVoice = async () => {
    if (!script.trim()) return;
    setPreviewing(true);
    try {
      const res = await fetch('/api/tts-timed', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: script }),
      });
      const blob = await res.blob();
      new Audio(URL.createObjectURL(blob)).play();
    } catch (e) { console.error(e); }
    finally { setPreviewing(false); }
  };

  const resetForm = () => {
    chosen.forEach((c) => { if (c.file) URL.revokeObjectURL(c.previewUrl); });
    setChosen([]);
    setScript('');
    setTeam1('');
    setTeam2('');
    setScore('');
    setGoalScorers(EMPTY_SCORERS);
    setShowScore(true);
    setShowFlags(true);
    setShowGoalScorers(true);
    setSelectedScriptKey(null);
    setSelectedQueries([]);
    setVideoSize('9:16');
    setOverlay(DEFAULT_OVERLAY);
    setMusic(RECAP_DEFAULT_MUSIC);
    setImagnResults([]);
    setImagnQuery('');
    setImagnPage(0);
    setGen({ stage: 'idle', progress: 0 });
  };

  // --- Generate video (tags the row category=<league>, run_date=date) ---
  const generateVideo = async () => {
    if (chosen.length === 0 || !script.trim()) return;
    try {
      startTimer();
      setGen({ stage: 'preparing', progress: 0, startedAt: Date.now() });

      const imageUrls: string[] = [];
      for (const item of chosen) {
        if (item.s3_url) {
          imageUrls.push(item.s3_url);
        } else if (item.imagnId) {
          const res = await fetch('/api/imagn/download', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ imageID: item.imagnId, caption: item.caption }),
          });
          imageUrls.push((await readJson<{ image: { s3_url: string } }>(res, 'Imagn download')).image.s3_url);
        } else if (item.file) {
          const form = new FormData();
          form.append('files', item.file);
          const res = await fetch('/api/image-library', { method: 'POST', body: form });
          imageUrls.push((await readJson<{ images: { s3_url: string }[] }>(res, 'Image upload')).images[0].s3_url);
        }
      }

      const scoreboardFields = scriptHasScoreboard(selectedScriptKey)
        ? { team1: resolveAbbr(sport.key, team1), team2: resolveAbbr(sport.key, team2), score, goalScorers, showScore, showFlags, showGoalScorers }
        : { team1: '', team2: '', score: '', goalScorers: EMPTY_SCORERS, showScore: false, showFlags: false, showGoalScorers: false };

      setGen((p) => ({ ...p, stage: 'audio' }));
      const createRes = await fetch('/api/image-videos', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ script, images: imageUrls, textOverlay: overlay, videoSize, status: 'draft', category: sport.key, runDate: date, ...scoreboardFields, ...(music.enabled || music.url ? { musicUrl: music.url, musicEnabled: music.enabled } : {}) }),
      });
      const { video } = await readJson<{ video: { id: string } }>(createRes, 'Saving the video');
      const id: string = video.id;

      const ttsRes = await fetch('/api/tts-timed', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: script, returnUrl: true }),
      });
      const { audioUrl, wordTimingsUrl } = await readJson<{ audioUrl: string; wordTimingsUrl: string }>(ttsRes, 'Audio generation');

      setGen((p) => ({ ...p, stage: 'rendering', progress: 0 }));
      const renderRes = await fetch('/api/render-images', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoId: id, audioUrl, wordTimingsUrl }),
      });
      const { renderId, bucketName } = await readJson<{ renderId: string; bucketName: string }>(renderRes, 'Starting the render');
      onRefresh();

      const poll = setInterval(async () => {
        try {
          const pr = await fetch(`/api/render-images?renderId=${renderId}&bucketName=${bucketName}&videoId=${id}`);
          const progress = await readJson<RenderProgress>(pr, 'Progress check');
          if (progress.fatalErrorEncountered) {
            clearInterval(poll); stopTimer();
            setGen((p) => ({ ...p, stage: 'error', progress: 0, error: progress.errors?.[0] || 'Render failed', finishedAt: Date.now() }));
            onRefresh();
            return;
          }
          if (progress.done && progress.outputFile) {
            clearInterval(poll); stopTimer();
            setGen((p) => ({ ...p, stage: 'done', progress: 100, videoUrl: progress.outputFile ?? undefined, fileSizeBytes: progress.outputSizeInBytes ?? undefined, finishedAt: Date.now() }));
            onRefresh();
            return;
          }
          setGen((p) => ({ ...p, stage: 'rendering', progress: Math.round((progress.overallProgress || 0) * 100), fileSizeBytes: progress.outputSizeInBytes ?? undefined }));
        } catch (e: unknown) {
          clearInterval(poll); stopTimer();
          setGen((p) => ({ ...p, stage: 'error', progress: 0, error: e instanceof Error ? e.message : 'Progress check failed', finishedAt: Date.now() }));
        }
      }, 2000);
    } catch (e: unknown) {
      stopTimer();
      setGen((p) => ({ ...p, stage: 'error', progress: 0, error: e instanceof Error ? e.message : 'Unknown error', finishedAt: Date.now() }));
    }
  };

  const deleteVideo = async (id: string) => {
    try { await fetch(`/api/image-videos?id=${id}`, { method: 'DELETE' }); onRefresh(); }
    catch (e) { console.error(e); }
  };

  const togglePublished = async (id: string, e: SyntheticEvent) => {
    e.stopPropagation();
    if (publishedBusy[id]) return;
    const next = !publishedById[id];
    setPublishedBusy((p) => ({ ...p, [id]: true }));
    setPublishedById((p) => ({ ...p, [id]: next }));
    try {
      const res = await fetch('/api/image-videos/published', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id, published: next }),
      });
      if (!res.ok) throw new Error();
    } catch {
      setPublishedById((p) => ({ ...p, [id]: !next }));
    } finally {
      setPublishedBusy((p) => ({ ...p, [id]: false }));
    }
  };

  const pushVideo = async (id: string) => {
    if (pushing[id]) return;
    setPushing((p) => ({ ...p, [id]: true }));
    setPushError((p) => { const n = { ...p }; delete n[id]; return n; });
    try {
      const res = await fetch('/api/image-videos/publish', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Push failed');
      setPublishedById((p) => ({ ...p, [id]: !!data.published }));
      onRefresh();
    } catch (e) {
      setPushError((p) => ({ ...p, [id]: e instanceof Error ? e.message : 'Push failed' }));
    } finally {
      setPushing((p) => ({ ...p, [id]: false }));
    }
  };

  const seedEdit = (v: ImageVideo): RowEdit =>
    ({ script: v.script ?? '', videoSize: v.video_size, overlay: v.text_overlay ?? DEFAULT_OVERLAY, music: musicFromVideo(v), scoreboard: scoreboardFromVideo(v) });
  const getEdit = (v: ImageVideo): RowEdit => editState[v.id] ?? seedEdit(v);
  const setEdit = (v: ImageVideo, patch: Partial<RowEdit>) =>
    setEditState((p) => ({ ...p, [v.id]: { ...(p[v.id] ?? seedEdit(v)), ...patch } }));

  const downloadVideo = async (e: ReactMouseEvent, url: string, filename: string, key: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (downloading[key]) return;
    setDownloading((p) => ({ ...p, [key]: true }));
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) { console.error(err); }
    finally { setDownloading((p) => ({ ...p, [key]: false })); }
  };

  const fileNameFor = (v: { id: string; script?: string | null }) => {
    const slug = (v.script ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
    return `${slug || v.id}.mp4`;
  };

  const loadSize = async (v: ImageVideo) => {
    if (!v.video_url || v.file_size_bytes || sizes[v.id] != null) return;
    try {
      const res = await fetch(v.video_url);
      const len = res.headers.get('content-length');
      res.body?.cancel().catch(() => {});
      if (len) setSizes((p) => ({ ...p, [v.id]: Number(len) }));
    } catch { /* ignore */ }
  };

  const runRender = async (v: ImageVideo, edits?: RowEdit) => {
    if (rowGen[v.id]?.stage === 'rendering') return;
    setRowGen((p) => ({ ...p, [v.id]: { stage: 'rendering', progress: 0 } }));
    try {
      const scriptVal = edits?.script ?? v.script ?? '';
      const videoSizeVal = edits?.videoSize ?? v.video_size;
      const overlayVal = edits?.overlay ?? v.text_overlay;

      if (edits) {
        const up = await fetch('/api/image-videos', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: v.id, script: scriptVal, textOverlay: overlayVal, videoSize: videoSizeVal,
            musicUrl: edits.music.url, musicEnabled: edits.music.enabled,
            team1: resolveAbbr(sport.key, edits.scoreboard.team1),
            team2: resolveAbbr(sport.key, edits.scoreboard.team2),
            score: edits.scoreboard.score,
            goalScorers: edits.scoreboard.goalScorers,
            showScore: edits.scoreboard.showScore, showFlags: edits.scoreboard.showFlags, showGoalScorers: edits.scoreboard.showGoalScorers,
          }),
        });
        await readJson(up, 'Saving changes');
      }

      let audioUrl = v.audio_url ?? undefined;
      let wordTimingsUrl = v.word_timings_url ?? undefined;
      if (!audioUrl || (edits && scriptVal !== (v.script ?? ''))) {
        const tts = await fetch('/api/tts-timed', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: scriptVal, returnUrl: true }),
        });
        await readJson(tts, 'Audio generation');
        ({ audioUrl, wordTimingsUrl } = await tts.json());
      }

      const res = await fetch('/api/render-images', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoId: v.id, audioUrl, wordTimingsUrl }),
      });
      const { renderId, bucketName } = await readJson<{ renderId: string; bucketName: string }>(res, 'Starting the render');
      onRefresh();
      const poll = setInterval(async () => {
        try {
          const pr = await fetch(`/api/render-images?renderId=${renderId}&bucketName=${bucketName}&videoId=${v.id}`);
          const progress = await readJson<RenderProgress>(pr, 'Progress check');
          if (progress.fatalErrorEncountered) {
            clearInterval(poll);
            setRowGen((p) => ({ ...p, [v.id]: { stage: 'error', progress: 0 } }));
            onRefresh();
            return;
          }
          if (progress.done && progress.outputFile) {
            clearInterval(poll);
            setRowGen((p) => { const n = { ...p }; delete n[v.id]; return n; });
            setEditState((p) => { const n = { ...p }; delete n[v.id]; return n; });
            onRefresh();
            return;
          }
          setRowGen((p) => ({ ...p, [v.id]: { stage: 'rendering', progress: Math.round((progress.overallProgress || 0) * 100) } }));
        } catch {
          clearInterval(poll);
          setRowGen((p) => ({ ...p, [v.id]: { stage: 'error', progress: 0 } }));
          onRefresh();
        }
      }, 2000);
    } catch {
      setRowGen((p) => ({ ...p, [v.id]: { stage: 'error', progress: 0 } }));
    }
  };

  const isGenerating = gen.stage === 'preparing' || gen.stage === 'audio' || gen.stage === 'rendering';

  const imagnTotalPages = Math.max(1, Math.ceil(imagnResults.length / IMAGN_PER_PAGE));
  const imagnPageSafe = Math.min(imagnPage, imagnTotalPages - 1);
  const imagnPageImages = imagnResults.slice(imagnPageSafe * IMAGN_PER_PAGE, imagnPageSafe * IMAGN_PER_PAGE + IMAGN_PER_PAGE);

  const latestRun = runs[0];
  const matchups: Matchup[] = latestRun ? mergeMatchups(latestRun) : [];

  const showScoreboard = scriptHasScoreboard(selectedScriptKey);
  const clockPlaceholder = sport.clockExamples[0];
  const scorerNoun = sport.scorerNoun;

  // Warn before generating if an abbreviation will not resolve to a logo.
  const missingLogos = [team1, team2].filter((t) => t.trim() && !hasLogo(sport.key, t));

  return (
    <div className="animate-in fade-in duration-500">
      {/* TOP BAR */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-8 py-4 border-b border-white/10 bg-white/[0.03]">
        <h3 className="text-[11px] font-black uppercase tracking-[0.15em] text-slate-400 flex items-center gap-2">
          <Shield size={14} className="text-sky-400" /> {sport.label} Recap Videos
        </h3>
        <button
          onClick={() => { setShowCreate((v) => !v); if (!libraryLoaded) loadLibrary(); }}
          className="flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all"
        >
          <Plus size={14} /> New Video
        </button>
      </div>

      {/* DATE + RUN PROMPTS BAR */}
      <div className="px-6 md:px-8 py-5 border-b border-white/10 bg-white/[0.015]">
        <StepHeader label={`${sport.label} Date`} />
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1 bg-slate-900/60 border border-white/10 rounded-lg px-2 focus-within:border-white/25 transition-colors">
            <button className="text-slate-500 hover:text-white p-1.5 hover:bg-white/5 rounded" onClick={() => setDate((d) => shiftDate(d, -1))} title="Previous day">
              <ChevronLeft size={16} />
            </button>
            <Calendar size={14} className="text-sky-400" />
            <input
              type="date"
              value={date}
              onChange={(e) => e.target.value && setDate(e.target.value)}
              className="bg-transparent py-2 px-1 text-sm text-white outline-none [color-scheme:dark]"
            />
            <button className="text-slate-500 hover:text-white p-1.5 hover:bg-white/5 rounded" onClick={() => setDate((d) => shiftDate(d, 1))} title="Next day">
              <ChevronRight size={16} />
            </button>
          </div>

          {/* The pro leagues run the whole slate in one go. CFB does not — its
              games are picked one at a time from the schedule below. */}
          {!sport.onDemand && (
            <button
              onClick={() => runPrompts()}
              disabled={running}
              className="bg-amber-500 hover:bg-amber-400 text-black px-5 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
            >
              {running ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} fill="currentColor" />}
              {running ? 'Running prompts…' : 'Run Prompts'}
            </button>
          )}
        </div>
        {runError && <p className="mt-2 text-[10px] text-red-400">{runError}</p>}

        {/* CFB SCHEDULE — Sportradar, on demand per game */}
        {sport.onDemand && (
          <div className="mt-5">
            <StepHeader
              label={`Schedule · ${prettyDate(date)}`}
              right={
                <span className="flex items-center gap-3 flex-wrap">
                  <span className="text-[10px] text-slate-600 normal-case tracking-normal">
                    {scheduleLoading
                      ? 'Loading…'
                      : `${visibleSchedule.length} of ${schedule.length} game${schedule.length === 1 ? '' : 's'} — run prompts for one at a time`}
                  </span>
                  <label
                    className={`flex items-center gap-1.5 text-[10px] font-black uppercase tracking-tighter ${rankingsAvailable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                    title={rankingsAvailable
                      ? 'Show only games with an AP Top 25 school on either side'
                      : 'The AP poll for this week is unavailable — showing every game'}
                  >
                    <input
                      type="checkbox"
                      checked={top25Active}
                      disabled={!rankingsAvailable}
                      onChange={(e) => setTop25Only(e.target.checked)}
                      className="accent-amber-500 w-3.5 h-3.5"
                    />
                    <span className={top25Active ? 'text-amber-500' : 'text-slate-500'}>Top 25 only</span>
                  </label>
                </span>
              }
            />
            {scheduleError ? (
              <p className="text-[11px] text-red-400 bg-red-500/5 border border-red-500/15 rounded-lg p-3">{scheduleError}</p>
            ) : scheduleLoading ? (
              <div className="flex items-center gap-2 text-slate-500 text-xs py-6 justify-center font-mono uppercase tracking-widest">
                <Loader2 size={14} className="animate-spin" /> Loading schedule…
              </div>
            ) : schedule.length === 0 ? (
              <div className="text-slate-600 text-xs py-8 text-center border border-dashed border-white/10 rounded-lg font-mono uppercase tracking-widest">
                No games scheduled on this date
              </div>
            ) : visibleSchedule.length === 0 ? (
              <div className="text-slate-600 text-xs py-8 text-center border border-dashed border-white/10 rounded-lg space-y-2">
                <p className="font-mono uppercase tracking-widest">No Top 25 games on this date</p>
                <button
                  onClick={() => setTop25Only(false)}
                  className="text-[10px] font-black uppercase tracking-wider text-amber-500 hover:text-amber-400 transition-colors"
                >
                  Show all {schedule.length} games
                </button>
              </div>
            ) : (
              <div className="max-h-[420px] overflow-y-auto rounded-lg border border-white/10 divide-y divide-white/5">
                {visibleSchedule.map((g) => {
                  const busy = running && runningGameId === g.id;
                  return (
                    <div key={g.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5 hover:bg-white/[0.02] transition-colors">
                      <span className="flex items-center gap-1.5 shrink-0">
                        <LogoThumb league={league} abbr={g.awayAlias} />
                        <span className="text-slate-600 text-[10px]">@</span>
                        <LogoThumb league={league} abbr={g.homeAlias} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-white truncate">
                          <Rank n={g.awayRank} />{g.awayName}{' '}
                          <span className="text-slate-600 font-normal">at</span>{' '}
                          <Rank n={g.homeRank} />{g.homeName}
                        </p>
                        <p className="text-[10px] font-mono text-slate-600 truncate">
                          {g.kickoff}
                          {g.week && ` · Wk ${g.week}`}
                          {g.network && ` · ${g.network}`}
                          {g.status && ` · ${g.status}`}
                        </p>
                      </div>
                      <button
                        onClick={() => runPrompts(g)}
                        disabled={running}
                        title="Run the preview + reaction prompts for this game"
                        className="shrink-0 bg-amber-500 hover:bg-amber-400 text-black px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all disabled:opacity-40 flex items-center gap-1.5"
                      >
                        {busy ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} fill="currentColor" />}
                        {busy ? 'Running…' : 'Run'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* CREATE PANEL */}
      {showCreate && (
        <div ref={createRef} className="px-6 md:px-8 py-6 border-b border-white/10 bg-white/[0.015] animate-in slide-in-from-top-2">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            <div className="order-2 lg:flex-1 min-w-0 space-y-6">
              {matchups.length > 0 && (
                <div>
                  <StepHeader label={`Pick a Script · ${prettyDate(latestRun!.run_date)}${latestRun!.game_label ? ` · ${latestRun!.game_label}` : ''}`} />
                  <QuickScriptSelect matchups={matchups} keyPrefix="latest" activeKey={selectedScriptKey} onPick={applyScript} league={league} />
                  <p className="mt-2 text-[10px] text-slate-600">
                    Click a script to drop it into the Audio Script below and suggest its image searches. Scroll to “Generated Outputs” to read &amp; compare the full text.
                  </p>
                </div>
              )}

              {/* IMAGE PICKER */}
              <div ref={pickerRef} className="scroll-mt-4">
                <StepHeader
                  label="Choose Images"
                  right={
                    <Segmented
                      options={['imagn', 'upload', 'library'] as PickerSource[]}
                      value={source}
                      onChange={(v) => { setSource(v); if (v === 'library' && !libraryLoaded) loadLibrary(); }}
                      labels={{ imagn: 'Imagn', upload: 'Upload', library: 'Library' }}
                    />
                  }
                />

                {source === 'imagn' && (
                  <div>
                    {selectedQueries.length > 0 && (
                      <div className="mb-3 rounded-lg border border-emerald-500/15 bg-emerald-500/[0.04] p-3">
                        <p className="text-[9px] font-black uppercase tracking-widest text-emerald-500/80 mb-2">
                          Suggested for the selected script · click to search
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {selectedQueries.map((q, i) => <QueryChip key={i} q={q} onClick={applyImagnQuery} />)}
                        </div>
                      </div>
                    )}
                    <div className="flex gap-2 mb-3">
                      <div className="flex-1 flex items-center gap-2 bg-slate-900/60 border border-white/10 rounded-lg px-3 focus-within:border-white/25 transition-colors">
                        <Search size={14} className="text-slate-500" />
                        <input
                          value={imagnQuery}
                          onChange={(e) => setImagnQuery(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && searchImagn()}
                          placeholder={`Search Imagn (e.g. ${sport.label} game action)`}
                          className="flex-1 bg-transparent py-2.5 text-sm text-white placeholder:text-slate-600 outline-none"
                        />
                      </div>
                      <button
                        onClick={() => searchImagn()}
                        disabled={imagnSearching || imagnQuery.trim().length < 2}
                        className="bg-white/10 hover:bg-white/20 text-white px-5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all disabled:opacity-40 flex items-center gap-2 border border-white/10"
                      >
                        {imagnSearching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                        Search
                      </button>
                    </div>
                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                      {imagnPageImages.map((img) => {
                        const sel = isChosen(`imagn:${img.imageID}`);
                        return (
                          <button
                            key={img.imageID}
                            onClick={() => addImagn(img)}
                            title={img.caption}
                            className={`relative aspect-square rounded-lg overflow-hidden border transition-all group ${
                              sel ? 'border-emerald-500/70 ring-2 ring-emerald-500/25' : 'border-white/10 hover:border-white/40'
                            }`}
                          >
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img src={img.thumbUrl} alt={img.caption} className="w-full h-full object-cover" />
                            {sel ? (
                              <div className="absolute top-1 right-1 bg-emerald-500 rounded-full p-0.5"><Check size={10} className="text-white" /></div>
                            ) : (
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"><Plus size={18} className="text-white" /></div>
                            )}
                          </button>
                        );
                      })}
                      {imagnResults.length === 0 && (
                        <div className="col-span-4 sm:col-span-6 text-center text-slate-600 text-xs py-10 font-mono uppercase tracking-widest px-4">
                          {imagnSearching
                            ? 'Searching…'
                            : lastSearched
                              ? `No images found for “${lastSearched}”`
                              : 'Search to find images'}
                        </div>
                      )}
                    </div>

                    {imagnResults.length > 0 && (
                      <div className="flex items-center justify-between gap-3 mt-3">
                        <span className="text-[10px] font-mono text-slate-600">
                          {imagnResults.length} result{imagnResults.length === 1 ? '' : 's'}
                          {imagnLoadingMore && <span className="text-slate-500"> · loading more…</span>}
                        </span>
                        {imagnTotalPages > 1 && (
                          <div className="flex items-center gap-2">
                            <button onClick={() => setImagnPage((p) => Math.max(0, p - 1))} disabled={imagnPageSafe === 0}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10 text-[10px] font-bold uppercase tracking-wider transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                              <ChevronLeft size={13} /> Prev
                            </button>
                            <span className="text-[10px] font-mono text-slate-400 tabular-nums min-w-[3.5rem] text-center">{imagnPageSafe + 1} / {imagnTotalPages}</span>
                            <button onClick={() => setImagnPage((p) => Math.min(imagnTotalPages - 1, p + 1))} disabled={imagnPageSafe >= imagnTotalPages - 1}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10 text-[10px] font-bold uppercase tracking-wider transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                              Next <ChevronRight size={13} />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {source === 'upload' && (
                  <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-white/10 rounded-xl py-12 cursor-pointer hover:border-white/30 hover:bg-white/[0.02] transition-all">
                    <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { handleUpload(e.target.files); e.currentTarget.value = ''; }} />
                    <Upload size={24} className="text-slate-500" />
                    <span className="text-xs text-slate-500 font-bold uppercase tracking-widest">Click to add images</span>
                  </label>
                )}

                {source === 'library' && (
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-[300px] overflow-y-auto">
                    {library.map((img) => {
                      const sel = isChosen(img.s3_url);
                      return (
                        <button
                          key={img.id}
                          onClick={() => (sel ? removeChosen(img.s3_url) : addChosen({ key: img.s3_url, previewUrl: img.s3_url, s3_url: img.s3_url, caption: img.caption ?? undefined }))}
                          title={img.caption ?? ''}
                          className={`relative aspect-square rounded-lg overflow-hidden border transition-all ${
                            sel ? 'border-emerald-500/70 ring-2 ring-emerald-500/25' : 'border-white/10 hover:border-white/30'
                          }`}
                        >
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img src={img.s3_url} alt="" className={`w-full h-full object-cover transition-opacity ${sel ? 'opacity-90' : ''}`} />
                          {sel && <div className="absolute top-1 right-1 bg-emerald-500 rounded-full p-0.5"><Check size={10} className="text-white" /></div>}
                        </button>
                      );
                    })}
                    {libraryLoaded && library.length === 0 && (
                      <div className="col-span-4 sm:col-span-6 text-center text-slate-600 text-xs py-10 font-mono uppercase tracking-widest">Library is empty</div>
                    )}
                  </div>
                )}
              </div>

              {/* ORDER */}
              <div>
                <StepHeader label={`Order (${chosen.length})`} right={chosen.length > 0 ? <span className="text-[10px] text-slate-600 normal-case tracking-normal">drag to reorder</span> : undefined} />
                {chosen.length === 0 ? (
                  <div className="text-slate-600 text-xs py-6 text-center border border-dashed border-white/10 rounded-lg font-mono uppercase tracking-widest">No images chosen yet</div>
                ) : (
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {chosen.map((img, i) => (
                      <div key={`${img.key}-${i}`} draggable onDragStart={() => onDragStart(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
                        className="relative shrink-0 w-24 h-24 rounded-lg overflow-hidden border border-white/10 cursor-move group">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src={img.previewUrl} alt="" className="w-full h-full object-cover pointer-events-none" />
                        <div className="absolute top-1 left-1 bg-black/60 rounded p-0.5"><GripVertical size={12} className="text-white/70" /></div>
                        <button onClick={() => removeChosen(img.key)} className="absolute top-1 right-1 bg-black/60 hover:bg-red-600/90 rounded p-0.5 transition-colors" title="Remove"><X size={12} className="text-white" /></button>
                        <div className="absolute bottom-1 left-1 bg-black/70 rounded px-1 text-[9px] font-mono text-white">{i + 1}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* SCOREBOARD — team1/team2 are logo abbreviations; the composition
                  resolves each emblem from the league + abbreviation. */}
              {showScoreboard && (
              <div>
                <StepHeader
                  label="Scoreboard"
                  right={
                    <div className="flex flex-wrap items-center gap-1.5">
                      <ToggleChip label="Score" active={showScore} onChange={setShowScore} />
                      <ToggleChip label="Logos" active={showFlags} onChange={setShowFlags} />
                      <ToggleChip label={`Show ${scorerNoun === 'leader' ? 'Leaders' : 'Scorers'}`} active={showGoalScorers} onChange={setShowGoalScorers} />
                    </div>
                  }
                />
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 items-end">
                  <label className="block">
                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Team 1 (abbr)</span>
                    <div className="mt-1 flex items-center gap-2 bg-slate-900/50 border border-white/10 rounded-lg px-2 focus-within:border-white/30 transition-colors">
                      <LogoThumb league={league} abbr={team1} />
                      <input value={team1} onChange={(e) => setTeam1(e.target.value.toUpperCase())} placeholder={sport.key === 'nfl' ? 'e.g. KC' : sport.key === 'cfb' ? 'e.g. ALA' : 'e.g. BOS'}
                        className="flex-1 min-w-0 bg-transparent py-2 text-sm text-white placeholder:text-slate-600 outline-none uppercase" />
                    </div>
                  </label>
                  <label className="block">
                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Score</span>
                    <input value={score} onChange={(e) => setScore(e.target.value)} placeholder="28-27"
                      className="mt-1 block w-full sm:w-24 text-center bg-slate-900/50 border border-white/10 rounded-lg px-2 py-2 text-sm font-mono text-white placeholder:text-slate-600 focus:border-white/30 outline-none" />
                  </label>
                  <label className="block">
                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Team 2 (abbr)</span>
                    <div className="mt-1 flex items-center gap-2 bg-slate-900/50 border border-white/10 rounded-lg px-2 focus-within:border-white/30 transition-colors">
                      <LogoThumb league={league} abbr={team2} />
                      <input value={team2} onChange={(e) => setTeam2(e.target.value.toUpperCase())} placeholder={sport.key === 'nfl' ? 'e.g. BUF' : sport.key === 'cfb' ? 'e.g. UGA' : 'e.g. LAL'}
                        className="flex-1 min-w-0 bg-transparent py-2 text-sm text-white placeholder:text-slate-600 outline-none uppercase" />
                    </div>
                  </label>
                </div>

                {missingLogos.length > 0 && (
                  <p className="mt-2 flex items-center gap-1.5 text-[10px] text-amber-400">
                    <AlertTriangle size={11} />
                    No logo uploaded for {missingLogos.join(', ')} — the scoreboard will render without that emblem.
                  </p>
                )}

                {/* SCORERS — grouped home/away, sent as the goalScorers prop */}
                <div className="mt-4">
                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">{sport.scorersLabel}</span>
                  <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <ScorerList side="home" teamName={team1} league={league} clockPlaceholder={clockPlaceholder} noun={scorerNoun} scorers={goalScorers.home}
                      onChange={(home) => setGoalScorers((g) => ({ ...g, home }))} />
                    <ScorerList side="away" teamName={team2} league={league} clockPlaceholder={clockPlaceholder} noun={scorerNoun} scorers={goalScorers.away}
                      onChange={(away) => setGoalScorers((g) => ({ ...g, away }))} />
                  </div>
                </div>
              </div>
              )}

              {/* SCRIPT */}
              <div>
                <StepHeader
                  label="Audio Script"
                  right={
                    <button onClick={previewVoice} disabled={previewing || !script.trim()}
                      className="flex items-center gap-2 bg-white/5 hover:bg-white/10 text-slate-300 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all disabled:opacity-40">
                      <Play size={12} className={previewing ? 'animate-spin' : ''} fill="currentColor" />
                      {previewing ? 'Synthesizing…' : 'Preview Voice'}
                    </button>
                  }
                />
                <textarea value={script} onChange={(e) => setScript(e.target.value)}
                  placeholder="Paste the audio script… or click “Use as script” on a generated Preview / Reaction below."
                  className="w-full bg-slate-900/50 border border-white/10 rounded-lg p-4 text-sm text-white placeholder:text-slate-600 focus:border-white/30 outline-none h-28 resize-none transition-all" />
              </div>

              <MusicPicker value={music} onChange={setMusic} builtins={[WORLDCUP_DEFAULT_TRACK]} />

              {/* GENERATE */}
              <div className="max-w-md mx-auto w-full pt-2">
                <button onClick={generateVideo} disabled={isGenerating || chosen.length === 0 || !script.trim()}
                  className="w-full bg-white text-black hover:bg-white/90 py-3 rounded-lg text-[11px] font-black uppercase tracking-widest transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                  {isGenerating ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} fill="currentColor" />}
                  {isGenerating ? 'Generating…' : 'Generate Video'}
                </button>

                {gen.stage !== 'idle' && gen.stage !== 'done' && (
                  <div className="space-y-2 mt-3">
                    <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-500 ${gen.stage === 'error' ? 'bg-red-500' : (gen.stage === 'preparing' || gen.stage === 'audio') ? 'bg-amber-500' : 'bg-sky-500'}`}
                        style={{ width: gen.stage === 'preparing' ? '8%' : gen.stage === 'audio' ? '15%' : gen.stage === 'error' ? '100%' : `${Math.max(20, gen.progress)}%` }} />
                    </div>
                    <p className="text-[10px] font-mono text-center tracking-wide">
                      {gen.stage === 'preparing' && <span className="text-amber-400">Preparing images…</span>}
                      {gen.stage === 'audio' && <span className="text-amber-400">Generating audio…</span>}
                      {gen.stage === 'rendering' && <span className="text-sky-400">Rendering video… {gen.progress}%</span>}
                      {gen.stage === 'error' && <span className="text-red-400">Error: {gen.error}</span>}
                    </p>
                    {gen.stage !== 'error' && (
                      <div className="flex items-center justify-center gap-4 text-[10px] font-mono text-slate-500">
                        <span className="flex items-center gap-1"><Timer size={10} />{formatElapsed(elapsed)}</span>
                        {gen.fileSizeBytes != null && gen.fileSizeBytes > 0 && <span className="flex items-center gap-1"><HardDrive size={10} />{formatFileSize(gen.fileSizeBytes)}</span>}
                      </div>
                    )}
                  </div>
                )}

                {gen.stage === 'done' && gen.videoUrl && (
                  <div className="space-y-2 mt-3">
                    <button onClick={(e) => downloadVideo(e, gen.videoUrl!, fileNameFor({ id: `${sport.key}-video`, script }), 'dl-gen')} disabled={downloading['dl-gen']}
                      className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-3 rounded-lg text-[11px] font-black uppercase tracking-widest transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-60">
                      {downloading['dl-gen'] ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                      {downloading['dl-gen'] ? 'Downloading…' : 'Download Video'}
                    </button>
                    <button onClick={resetForm} className="w-full text-slate-500 hover:text-white text-[10px] font-bold uppercase tracking-widest py-2 transition-colors">Start another</button>
                  </div>
                )}
              </div>
            </div>

            {/* OPTIONS COLUMN */}
            <div className="order-1 lg:w-max lg:shrink-0">
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-4">
                <div>
                  <span className="text-xs font-semibold text-slate-200 block mb-2">Video size</span>
                  <Segmented options={VIDEO_SIZES} value={videoSize} onChange={setVideoSize} icon={(o) => <RatioBox size={o} />} />
                </div>
                <div className="pt-1 border-t border-white/5">
                  <span className="text-xs font-semibold text-slate-200 block mb-2 mt-3">Text position</span>
                  <Segmented options={POSITIONS} value={overlay.position} onChange={(v) => setOverlay((o) => ({ ...o, position: v }))} icon={(o) => <PositionGlyph position={o} />} />
                </div>
                <div className="pt-1 border-t border-white/5">
                  <span className="text-xs font-semibold text-slate-200 block mb-2 mt-3">Word breaking</span>
                  <Segmented options={WORD_BREAKING} value={overlay.wordBreaking} onChange={(v) => setOverlay((o) => ({ ...o, wordBreaking: v }))} />
                </div>
                <div className="pt-1 border-t border-white/5">
                  <span className="text-xs font-semibold text-slate-200 block mb-2 mt-3">Font size</span>
                  <Segmented options={FONT_SIZES} value={overlay.size} onChange={(v) => setOverlay((o) => ({ ...o, size: v }))} icon={(o) => <FontGlyph size={o} />} />
                </div>
                <div className="pt-1 border-t border-white/5">
                  <span className="text-xs font-semibold text-slate-200 block mb-2 mt-3">Font color</span>
                  <div className="flex items-center gap-3">
                    <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(overlay.color) ? overlay.color : '#FFFFFF'} onChange={(e) => setOverlay((o) => ({ ...o, color: e.target.value }))}
                      className="w-10 h-8 rounded bg-transparent border border-white/10 cursor-pointer" />
                    <input type="text" value={overlay.color} onChange={(e) => setOverlay((o) => ({ ...o, color: e.target.value }))} spellCheck={false} placeholder="#FFFFFF"
                      className="w-24 bg-slate-900/50 border border-white/10 rounded px-2 py-1.5 font-mono text-xs text-slate-200 placeholder:text-slate-600 focus:border-white/30 outline-none" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EXISTING VIDEOS LIST */}
      <div className="hidden md:grid grid-cols-12 px-8 py-3 text-[10px] font-black uppercase tracking-tighter text-slate-500 border-b border-white/5">
        <div className="col-span-1">Preview</div>
        <div className="col-span-3">Script</div>
        <div className="col-span-1">Size</div>
        <div className="col-span-2">Status</div>
        <div className="col-span-2">Rendered</div>
        <div className="col-span-3 text-right">Action</div>
      </div>

      <div className="divide-y divide-white/5">
        {videos.length === 0 ? (
          <div className="p-16 text-center text-slate-600 font-mono text-xs uppercase tracking-widest flex flex-col items-center gap-3">
            <ImageIcon size={28} className="opacity-40" />
            No {sport.label} videos yet — choose images and a script, then click “Generate Video”
          </div>
        ) : videos.map((v) => {
          const rg = rowGen[v.id];
          const isRendering = rg?.stage === 'rendering' || v.status === 'rendering';
          const failed = rg?.stage === 'error' || (v.status === 'error' && !rg);
          const expanded = expandedRow === v.id;
          const ed = getEdit(v);
          const setSb = (patch: Partial<ScoreboardEdit>) => setEdit(v, { scoreboard: { ...ed.scoreboard, ...patch } });
          const fileSize = v.file_size_bytes ?? sizes[v.id];
          return (
          <div key={v.id} className="group">
            <div className="grid grid-cols-12 px-8 py-4 items-center hover:bg-white/[0.02] transition-colors cursor-pointer"
              onClick={() => { const o = !expanded; setExpandedRow(o ? v.id : null); if (o) loadSize(v); }}>
              <div className="col-span-1">
                <div className="w-12 h-12 rounded-lg overflow-hidden border border-white/10 bg-black">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  {v.images?.[0] && <img src={v.images[0]} alt="" className="w-full h-full object-cover" />}
                </div>
              </div>
              <div className="col-span-3 pr-4">
                <p className="text-xs text-slate-300 line-clamp-2 italic">{v.script || '— no script —'}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] font-mono text-slate-600">{v.images?.length ?? 0} imgs</span>
                  {(v.team1 || v.team2) && (
                    <span className="flex items-center gap-1">
                      <LogoThumb league={league} abbr={v.team1 ?? ''} />
                      <LogoThumb league={league} abbr={v.team2 ?? ''} />
                    </span>
                  )}
                  <PublishBadge published={!!publishedById[v.id]} v={v} />
                </div>
              </div>
              <div className="col-span-1 flex items-center gap-2 text-slate-400">
                <RatioBox size={v.video_size} />
                <span className="text-[11px] font-mono">{v.video_size}</span>
              </div>
              <div className="col-span-2">
                <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${
                  v.status === 'done' ? 'bg-emerald-600/20 text-emerald-400' : isRendering ? 'bg-sky-600/20 text-sky-400' : failed ? 'bg-red-600/20 text-red-400' : 'bg-slate-700/40 text-slate-400'
                }`}>
                  {isRendering ? (rg ? `rendering ${rg.progress}%` : 'rendering') : failed ? 'error' : v.status}
                </span>
              </div>
              <div className="col-span-2 text-[10px] font-mono text-slate-500">
                {v.status === 'done' && v.video_url ? new Date(v.updated_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
              </div>
              <div className="col-span-3 flex justify-end gap-1 items-center" onClick={(e) => e.stopPropagation()}>
                {failed && (
                  <button onClick={() => runRender(v)} disabled={isRendering} title="Retry render"
                    className="flex items-center gap-1 px-2 py-1.5 rounded text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 transition-colors text-[10px] font-bold uppercase tracking-wider disabled:opacity-40">
                    <RotateCcw size={13} /> Retry
                  </button>
                )}
                {isRendering && <Loader2 size={14} className="animate-spin text-sky-400" />}
                {v.video_url && (
                  <button onClick={() => pushVideo(v.id)} disabled={pushing[v.id]} title="Push to Instagram + Facebook"
                    className="flex items-center gap-1 px-2 py-1.5 rounded text-fuchsia-400 hover:text-fuchsia-300 hover:bg-fuchsia-500/10 transition-colors text-[10px] font-bold uppercase tracking-wider disabled:opacity-50">
                    {pushing[v.id] ? <Loader2 size={13} className="animate-spin" /> : <Share2 size={13} />}
                    {pushing[v.id] ? '' : 'Push'}
                  </button>
                )}
                {v.video_url && (
                  <button onClick={() => setViewing(v)} title="View video" className="p-1.5 rounded text-sky-400 hover:text-sky-300 hover:bg-sky-500/10 transition-colors"><Eye size={14} /></button>
                )}
                {v.video_url && (
                  <button onClick={(e) => downloadVideo(e, v.video_url!, fileNameFor(v), `dl-${v.id}`)} disabled={downloading[`dl-${v.id}`]} title="Download video"
                    className="p-1.5 rounded text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors disabled:opacity-50">
                    {downloading[`dl-${v.id}`] ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  </button>
                )}
                <label className="flex items-center gap-1.5 cursor-pointer text-[10px] font-black uppercase tracking-tighter pl-1" onClick={(e) => e.stopPropagation()} title="Mark as published (excludes from auto-publish)">
                  <input type="checkbox" checked={!!publishedById[v.id]} disabled={publishedBusy[v.id]} onChange={(e) => togglePublished(v.id, e)} onClick={(e) => e.stopPropagation()} className="accent-emerald-500 w-3.5 h-3.5" />
                  <span className={publishedById[v.id] ? 'text-emerald-500' : 'text-slate-500'}>Published</span>
                </label>
                <button onClick={() => deleteVideo(v.id)} title="Delete" className="p-1.5 rounded text-slate-600 hover:text-red-500 hover:bg-red-500/10 transition-colors"><Trash2 size={14} /></button>
                <ChevronDown size={14} className={`text-slate-600 transition-transform ${expanded ? 'rotate-180' : ''}`} />
              </div>
            </div>

            {pushError[v.id] && (
              <div className="px-8 pb-3 -mt-2"><p className="text-[10px] text-red-400 text-right">Push failed: {pushError[v.id]}</p></div>
            )}

            {expanded && (
              <div className="px-8 pb-6 pt-2 bg-black/30 border-t border-white/5 animate-in slide-in-from-top-1">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                  <div className="space-y-2">
                    <h5 className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Preview</h5>
                    {v.video_url ? (
                      <video src={v.video_url} controls preload="metadata" className="w-full max-h-[440px] rounded-lg border border-white/10 bg-black object-contain"
                        onLoadedMetadata={(e) => { const d = e.currentTarget.duration; setDurations((p) => (p[v.id] == null ? { ...p, [v.id]: d } : p)); }} />
                    ) : (
                      <div className="w-full aspect-[9/16] rounded-lg border border-dashed border-white/10 bg-black/40 flex items-center justify-center text-center text-slate-600 text-[10px] font-mono uppercase tracking-widest px-2">
                        {isRendering ? 'Rendering…' : 'Not rendered yet'}
                      </div>
                    )}
                  </div>

                  <div className="lg:col-span-2 space-y-4">
                    <div>
                      <h5 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Script</h5>
                      <textarea value={ed.script} onChange={(e) => setEdit(v, { script: e.target.value })}
                        className="w-full bg-slate-900/50 border border-white/10 rounded-lg p-3 text-xs text-slate-200 placeholder:text-slate-600 focus:border-white/30 outline-none h-24 resize-none" />
                    </div>

                    {videoHasScoreboard(v) && (
                    <div>
                      <div className="flex items-center justify-between gap-3 flex-wrap mb-2 min-h-[24px]">
                        <h5 className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Scoreboard</h5>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <ToggleChip label="Score" active={ed.scoreboard.showScore} onChange={(val) => setSb({ showScore: val })} />
                          <ToggleChip label="Logos" active={ed.scoreboard.showFlags} onChange={(val) => setSb({ showFlags: val })} />
                          <ToggleChip label={`Show ${scorerNoun === 'leader' ? 'Leaders' : 'Scorers'}`} active={ed.scoreboard.showGoalScorers} onChange={(val) => setSb({ showGoalScorers: val })} />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 items-end">
                        <label className="block">
                          <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Team 1 (abbr)</span>
                          <div className="mt-1 flex items-center gap-2 bg-slate-900/50 border border-white/10 rounded-lg px-2 focus-within:border-white/30 transition-colors">
                            <LogoThumb league={league} abbr={ed.scoreboard.team1} />
                            <input value={ed.scoreboard.team1} onChange={(e) => setSb({ team1: e.target.value.toUpperCase() })} placeholder="e.g. KC"
                              className="flex-1 min-w-0 bg-transparent py-2 text-sm text-white placeholder:text-slate-600 outline-none uppercase" />
                          </div>
                        </label>
                        <label className="block">
                          <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Score</span>
                          <input value={ed.scoreboard.score} onChange={(e) => setSb({ score: e.target.value })} placeholder="28-27"
                            className="mt-1 block w-full sm:w-24 text-center bg-slate-900/50 border border-white/10 rounded-lg px-2 py-2 text-sm font-mono text-white placeholder:text-slate-600 focus:border-white/30 outline-none" />
                        </label>
                        <label className="block">
                          <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Team 2 (abbr)</span>
                          <div className="mt-1 flex items-center gap-2 bg-slate-900/50 border border-white/10 rounded-lg px-2 focus-within:border-white/30 transition-colors">
                            <LogoThumb league={league} abbr={ed.scoreboard.team2} />
                            <input value={ed.scoreboard.team2} onChange={(e) => setSb({ team2: e.target.value.toUpperCase() })} placeholder="e.g. BUF"
                              className="flex-1 min-w-0 bg-transparent py-2 text-sm text-white placeholder:text-slate-600 outline-none uppercase" />
                          </div>
                        </label>
                      </div>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <ScorerList side="home" teamName={ed.scoreboard.team1} league={league} clockPlaceholder={clockPlaceholder} noun={scorerNoun} scorers={ed.scoreboard.goalScorers.home}
                          onChange={(home) => setSb({ goalScorers: { ...ed.scoreboard.goalScorers, home } })} />
                        <ScorerList side="away" teamName={ed.scoreboard.team2} league={league} clockPlaceholder={clockPlaceholder} noun={scorerNoun} scorers={ed.scoreboard.goalScorers.away}
                          onChange={(away) => setSb({ goalScorers: { ...ed.scoreboard.goalScorers, away } })} />
                      </div>
                    </div>
                    )}

                    <div>
                      <h5 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Images ({v.images?.length ?? 0})</h5>
                      <div className="flex flex-wrap gap-2">
                        {(v.images ?? []).map((url, i) => (
                          <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-white/10">
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img src={url} alt="" className="w-full h-full object-cover" />
                            <div className="absolute bottom-0.5 left-0.5 bg-black/70 rounded px-1 text-[8px] font-mono text-white">{i + 1}</div>
                          </div>
                        ))}
                      </div>
                      <p className="text-[10px] text-slate-600 mt-1.5">To change images, create a new video.</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h5 className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Options</h5>
                    <div className="rounded-xl border border-white/10 bg-white/[0.02] divide-y divide-white/5">
                      <div className="flex items-center justify-between gap-3 flex-wrap px-3 py-2">
                        <span className="text-xs font-semibold text-slate-200">Video size</span>
                        <Segmented compact options={VIDEO_SIZES} value={ed.videoSize} onChange={(val) => setEdit(v, { videoSize: val })} icon={(o) => <RatioBox size={o} />} />
                      </div>
                      <div className="flex items-center justify-between gap-3 flex-wrap px-3 py-2">
                        <span className="text-xs font-semibold text-slate-200">Text position</span>
                        <Segmented compact options={POSITIONS} value={ed.overlay.position} onChange={(val) => setEdit(v, { overlay: { ...ed.overlay, position: val } })} icon={(o) => <PositionGlyph position={o} />} />
                      </div>
                      <div className="flex items-center justify-between gap-3 flex-wrap px-3 py-2">
                        <span className="text-xs font-semibold text-slate-200">Word breaking</span>
                        <Segmented compact options={WORD_BREAKING} value={ed.overlay.wordBreaking} onChange={(val) => setEdit(v, { overlay: { ...ed.overlay, wordBreaking: val } })} />
                      </div>
                      <div className="flex items-center justify-between gap-3 flex-wrap px-3 py-2">
                        <span className="text-xs font-semibold text-slate-200">Font size</span>
                        <Segmented compact options={FONT_SIZES} value={ed.overlay.size} onChange={(val) => setEdit(v, { overlay: { ...ed.overlay, size: val } })} icon={(o) => <FontGlyph size={o} />} />
                      </div>
                      <div className="flex items-center justify-between gap-3 flex-wrap px-3 py-2">
                        <span className="text-xs font-semibold text-slate-200">Font color</span>
                        <div className="flex items-center gap-2">
                          <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(ed.overlay.color) ? ed.overlay.color : '#FFFFFF'} onChange={(e) => setEdit(v, { overlay: { ...ed.overlay, color: e.target.value } })}
                            className="w-8 h-7 rounded bg-transparent border border-white/10 cursor-pointer" />
                          <input type="text" value={ed.overlay.color} onChange={(e) => setEdit(v, { overlay: { ...ed.overlay, color: e.target.value } })} spellCheck={false}
                            className="w-24 bg-slate-900/50 border border-white/10 rounded px-2 py-1 font-mono text-[11px] text-slate-200 focus:border-white/30 outline-none" />
                        </div>
                      </div>
                    </div>

                    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                      <MusicPicker value={ed.music} onChange={(m) => setEdit(v, { music: m })} builtins={[WORLDCUP_DEFAULT_TRACK]} />
                    </div>

                    <button onClick={() => runRender(v, ed)} disabled={isRendering || !ed.script.trim()}
                      className="w-full mt-1 bg-white text-black hover:bg-white/90 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                      {isRendering ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} fill="currentColor" />}
                      {isRendering ? `Rendering ${rg?.progress ?? 0}%` : 'Re-generate Video'}
                    </button>
                    {rg?.stage === 'error' && <p className="text-[10px] text-red-400 text-center">Render failed — try again.</p>}

                    <div className="pt-1 text-[10px] text-slate-500 space-y-1">
                      {v.status === 'done' && v.video_url && (
                        <div className="flex items-center gap-4 text-slate-400">
                          {durations[v.id] != null && isFinite(durations[v.id]) ? <span className="flex items-center gap-1" title="Duration"><Clock size={10} />{formatDuration(durations[v.id])}</span> : null}
                          {fileSize ? <span className="flex items-center gap-1" title="File size"><HardDrive size={10} />{formatFileSize(fileSize)}</span> : null}
                        </div>
                      )}
                      <p>Created {new Date(v.created_at).toLocaleString()}</p>
                      {v.published_at && <p className="text-emerald-500">Published {new Date(v.published_at).toLocaleString()}</p>}
                      {v.publish_error && <p className="text-red-400">Publish note: {v.publish_error}</p>}
                      {(v.audio_url || v.word_timings_url) && (
                        <div className="flex gap-3 pt-0.5">
                          {v.audio_url && <a href={v.audio_url} target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline flex items-center gap-1"><ExternalLink size={10} /> Audio</a>}
                          {v.word_timings_url && <a href={v.word_timings_url} target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline flex items-center gap-1"><ExternalLink size={10} /> Timings</a>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          );
        })}
      </div>

      {/* GENERATED OUTPUTS (prompt runs) */}
      <div className="px-6 md:px-8 py-5 border-t border-white/10">
        <h3 className="text-[11px] font-black uppercase tracking-[0.15em] text-slate-400 mb-4 flex items-center gap-2">
          <FileJson size={14} className="text-sky-400" /> Generated Outputs
          {runs.length > 0 && <span className="text-slate-600 font-mono">({runs.length})</span>}
        </h3>

        {runs.length === 0 ? (
          <div className="text-slate-600 text-xs py-8 text-center border border-dashed border-white/10 rounded-lg font-mono uppercase tracking-widest">
            {sport.onDemand
              ? 'No runs yet — pick a date, then hit “Run” on a game above'
              : 'No runs yet — pick a date and hit “Run Prompts”'}
          </div>
        ) : (
          <div className="space-y-2">
            {runs.map((run) => {
              const open = expandedRun === run.id;
              const view = runView[run.id] ?? 'both';
              const setView = (v: 'both' | 'preview' | 'reaction') => setRunView((p) => ({ ...p, [run.id]: v }));
              const previewMatches = (run.preview_json ?? []) as RecapPreviewMatch[];
              const reactionMatches = (run.reaction_json ?? []) as RecapReactionMatch[];
              // Rows written before the async migration have no status; they
              // are all finished, so they read as 'done'.
              const status = run.status ?? 'done';
              const inFlight = status === 'running';
              const warnings = run.warnings ?? [];
              return (
                <div key={run.id} className="border border-white/10 rounded-lg bg-white/[0.02] overflow-hidden">
                  <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/[0.02]" onClick={() => setExpandedRun(open ? null : run.id)}>
                    <ListChecks size={14} className="text-sky-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-white truncate">
                        {prettyDate(run.run_date)}
                        {run.game_label && <span className="text-slate-500 font-normal"> · {run.game_label}</span>}
                      </p>
                      <p className="text-[10px] font-mono text-slate-600">{new Date(run.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                    <div className="flex items-center gap-1.5 ml-auto">
                      {inFlight ? (
                        <span className="px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest bg-sky-600/20 text-sky-400 inline-flex items-center gap-1.5">
                          <Loader2 size={10} className="animate-spin" />
                          {previewMatches.length > 0 ? `Recapping ${previewMatches.length} games` : 'Running'}
                        </span>
                      ) : (
                        <>
                          <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${run.preview_error ? 'bg-red-600/20 text-red-400' : 'bg-sky-600/20 text-sky-400'}`}>
                            Preview {run.preview_error ? '✕' : previewMatches.length}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${run.reaction_error ? 'bg-red-600/20 text-red-400' : 'bg-amber-600/20 text-amber-400'}`}>
                            Reaction {run.reaction_error ? '✕' : reactionMatches.length}
                          </span>
                          {/* A slate that came back short used to be invisible —
                              the run looked clean and the missing games were
                              only noticed when someone went looking for one. */}
                          {warnings.length > 0 && (
                            <span title={warnings.join('\n')}
                              className="px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest bg-amber-500/20 text-amber-300 inline-flex items-center gap-1">
                              <AlertTriangle size={10} /> {warnings.length}
                            </span>
                          )}
                        </>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); deleteRun(run.id); }} title="Delete run"
                        className="p-1.5 rounded text-slate-600 hover:text-red-500 hover:bg-red-500/10 transition-colors"><Trash2 size={14} /></button>
                      <ChevronDown size={14} className={`text-slate-600 transition-transform ${open ? 'rotate-180' : ''}`} />
                    </div>
                  </div>

                  {open && (
                    <div className="px-4 pb-5 pt-1 border-t border-white/5 bg-black/20 animate-in slide-in-from-top-1">
                      {inFlight && (
                        <p className="my-3 text-[11px] font-mono text-sky-400 inline-flex items-center gap-2">
                          <Loader2 size={12} className="animate-spin" />
                          {previewMatches.length > 0
                            ? `Writing a recap for each of ${previewMatches.length} games — they appear here as they land.`
                            : 'Reading the slate…'}
                        </p>
                      )}
                      {warnings.length > 0 && (
                        <div className="my-3 rounded-lg border border-amber-500/20 bg-amber-500/[0.06] px-3 py-2.5">
                          <p className="text-[9px] font-black uppercase tracking-widest text-amber-400 mb-1.5 inline-flex items-center gap-1.5">
                            <AlertTriangle size={11} /> Came back short
                          </p>
                          <ul className="space-y-1">
                            {warnings.map((w, i) => (
                              <li key={i} className="text-[11px] text-amber-200/80 leading-relaxed">{w}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {(() => { const mus = mergeMatchups(run); return mus.length > 0 ? (
                        <div className="my-3">
                          <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-2">Pick a Script</p>
                          <QuickScriptSelect matchups={mus} keyPrefix={`run:${run.id}`} activeKey={selectedScriptKey} onPick={applyScript} league={league} />
                        </div>
                      ) : null; })()}

                      <div className="flex items-center gap-2 my-3">
                        <span className="text-[9px] font-black uppercase tracking-widest text-slate-600">Read all</span>
                        <Segmented options={['both', 'preview', 'reaction'] as ('both' | 'preview' | 'reaction')[]} value={view} onChange={setView}
                          labels={{ both: 'Both', preview: 'Preview', reaction: 'Reaction' }} />
                      </div>

                      <div className={`grid gap-5 ${view === 'both' ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>
                        {view !== 'reaction' && (
                          <div className="space-y-3">
                            <div className="flex items-center justify-between gap-2">
                              <h4 className="text-[10px] font-black uppercase tracking-widest text-sky-400">Preview</h4>
                              {run.preview_url && <a href={run.preview_url} target="_blank" rel="noopener noreferrer" className="text-[9px] text-sky-500 hover:underline flex items-center gap-1"><ExternalLink size={9} /> JSON file</a>}
                            </div>
                            {run.preview_error ? (
                              <p className="text-[11px] text-red-400 bg-red-500/5 border border-red-500/15 rounded-lg p-3">{run.preview_error}</p>
                            ) : previewMatches.length === 0 ? (
                              <p className="text-[11px] text-slate-600 italic">No matches returned.</p>
                            ) : (
                              previewMatches.map((m, i) => (
                                <div key={i} className="rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-2.5">
                                  <div className="flex items-baseline justify-between gap-2">
                                    <span className="text-sm font-bold text-white">{m.teams}</span>
                                    {m.match_time && <span className="text-[10px] font-mono text-slate-400">{m.match_time}</span>}
                                  </div>
                                  <ScriptBlock label="Preview Narrative" text={m.preview_narrative_script} onUse={(t) => applyScript(t, m.image_search_queries, `det:${run.id}:${i}:preview`)} />
                                  <ScriptBlock label="Betting Line" text={m.betting_line_movement} onUse={(t) => applyScript(t, m.image_search_queries, `det:${run.id}:${i}:betting`)} />
                                  {(m.image_search_queries ?? []).length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 pt-0.5">
                                      {(m.image_search_queries ?? []).map((q, qi) => <QueryChip key={qi} q={q} onClick={applyImagnQuery} />)}
                                    </div>
                                  )}
                                </div>
                              ))
                            )}
                            <details className="group">
                              <summary className="cursor-pointer text-[9px] font-bold uppercase tracking-widest text-slate-600 hover:text-slate-400 select-none">Raw JSON</summary>
                              <pre className="mt-2 max-h-72 overflow-auto rounded-lg bg-black/50 border border-white/10 p-3 text-[10px] leading-relaxed text-slate-400 font-mono">{JSON.stringify(run.preview_json, null, 2)}</pre>
                            </details>
                          </div>
                        )}

                        {view !== 'preview' && (
                          <div className="space-y-3">
                            <div className="flex items-center justify-between gap-2">
                              <h4 className="text-[10px] font-black uppercase tracking-widest text-amber-400">Reaction</h4>
                              {run.reaction_url && <a href={run.reaction_url} target="_blank" rel="noopener noreferrer" className="text-[9px] text-amber-500 hover:underline flex items-center gap-1"><ExternalLink size={9} /> JSON file</a>}
                            </div>
                            {run.reaction_error ? (
                              <p className="text-[11px] text-red-400 bg-red-500/5 border border-red-500/15 rounded-lg p-3">{run.reaction_error}</p>
                            ) : reactionMatches.length === 0 ? (
                              <p className="text-[11px] text-slate-600 italic">No matches returned.</p>
                            ) : (
                              reactionMatches.map((m, i) => (
                                <div key={i} className="rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-2.5">
                                  <div className="flex items-baseline justify-between gap-2">
                                    <span className="text-sm font-bold text-white">{m.teams}</span>
                                    {m.verified_score && <span className="text-[11px] font-mono font-bold text-emerald-400">{m.verified_score}</span>}
                                  </div>
                                  <ScriptBlock label="Recap (Factual)" text={m.recap_script_low_temp} onUse={(t) => applyScript(t, m.image_search_queries, `det:${run.id}:${i}:recap`)} />
                                  <ScriptBlock label="Hot Take" text={m.hot_take_high_temp} onUse={(t) => applyScript(t, m.image_search_queries, `det:${run.id}:${i}:hot`)} />
                                  {(m.image_search_queries ?? []).length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 pt-0.5">
                                      {(m.image_search_queries ?? []).map((q, qi) => <QueryChip key={qi} q={q} onClick={applyImagnQuery} />)}
                                    </div>
                                  )}
                                </div>
                              ))
                            )}
                            <details className="group">
                              <summary className="cursor-pointer text-[9px] font-bold uppercase tracking-widest text-slate-600 hover:text-slate-400 select-none">Raw JSON</summary>
                              <pre className="mt-2 max-h-72 overflow-auto rounded-lg bg-black/50 border border-white/10 p-3 text-[10px] leading-relaxed text-slate-400 font-mono">{JSON.stringify(run.reaction_json, null, 2)}</pre>
                            </details>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* VIEW OVERLAY */}
      {viewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in" onClick={() => setViewing(null)}>
          <div className="relative bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-white/10">
              <p className="text-xs text-slate-300 italic line-clamp-1">{viewing.script || '— no script —'}</p>
              <button onClick={() => setViewing(null)} title="Close" className="shrink-0 p-1.5 rounded text-slate-500 hover:text-white hover:bg-white/10 transition-colors"><X size={18} /></button>
            </div>
            <div className="flex-1 min-h-0 bg-black flex items-center justify-center">
              {viewing.video_url ? (
                <video src={viewing.video_url} controls autoPlay className="max-h-[70vh] max-w-full" />
              ) : (
                <p className="text-slate-600 text-xs font-mono uppercase tracking-widest py-20">No video available</p>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/10">
              {viewing.video_url && (
                <button onClick={() => pushVideo(viewing.id)} disabled={pushing[viewing.id]}
                  className="flex items-center gap-2 bg-fuchsia-600 hover:bg-fuchsia-500 text-white px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-60">
                  {pushing[viewing.id] ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />}
                  {pushing[viewing.id] ? 'Pushing…' : 'Push'}
                </button>
              )}
              {viewing.video_url && (
                <button onClick={(e) => downloadVideo(e, viewing.video_url!, fileNameFor(viewing), `dl-modal-${viewing.id}`)} disabled={downloading[`dl-modal-${viewing.id}`]}
                  className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-60">
                  {downloading[`dl-modal-${viewing.id}`] ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  {downloading[`dl-modal-${viewing.id}`] ? 'Downloading…' : 'Download'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
